import {
  ApiRef,
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import {
  Configuration,
  IPFiltersApi,
  IpfilterIngressFilter,
  ResponseError as SdkResponseError,
} from '@internal/backstage-plugin-neo4j-networking-common';

import { TrafficConfigApiError } from './errors';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Resolves a user-facing error message for traffic-config-api GET failures.
 *
 * SDK GET endpoints use three error patterns in routes.go:
 *
 * 1. IP filter ingress-config (`/v1/ip-filters/...`)
 *    Returns an empty JSON object or array on failure; only the status code
 *    carries meaning. Callers should supply per-status fallbacks.
 *
 * 2. IP filter traffic type (`/v1/ip-filter-traffic-types/...`)
 *    Returns `{ error, details }` via handleGeneralError.
 *
 * 3. Traffic config (`/v1/isolation-units/.../traffic-config`,
 *    `/v1/orchestras/.../traffic-config`)
 *    Returns Echo validation errors as plain text or `{ message }`.
 *
 * When the body carries a usable message it takes precedence; otherwise the
 * caller's per-status fallbacks are used, then a generic default.
 */
async function getTrafficConfigErrorMessage(
  response: Response,
  fallbacks: Partial<Record<number, string>> = {},
): Promise<string> {
  const fallback =
    fallbacks[response.status] ?? response.statusText ?? 'Request failed';

  let rawBody = '';
  try {
    rawBody = await response.clone().text();
  } catch {
    return fallback;
  }

  if (rawBody.trim() === '') {
    return fallback;
  }

  let body: unknown = rawBody;
  try {
    body = JSON.parse(rawBody);
  } catch {
    return rawBody.trim() || fallback;
  }

  if (typeof body === 'string') {
    return body || fallback;
  }

  if (!isRecord(body)) {
    return fallback;
  }

  if (typeof body.error === 'string' && body.error) {
    if (typeof body.details === 'string' && body.details) {
      return `${body.error}: ${body.details}`;
    }

    return body.error;
  }

  if (typeof body.message === 'string' && body.message) {
    return body.message;
  }

  return fallback;
}

export class TrafficConfigRestApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApiBackstage: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApiBackstage = options.fetchApi;
  }

  private async getIPFiltersApi(): Promise<IPFiltersApi> {
    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');

    return new IPFiltersApi(
      new Configuration({
        basePath: `${baseUrl}/traffic-config-api`,
        fetchApi: this.fetchApiBackstage.fetch,
      }),
    );
  }

  private async handleSdkError(
    error: unknown,
    fallbacks: Partial<Record<number, string>> = {},
  ): Promise<never> {
    if (error instanceof SdkResponseError) {
      const errorMessage = await getTrafficConfigErrorMessage(
        error.response,
        fallbacks,
      );

      throw new TrafficConfigApiError(errorMessage, error.response.status);
    }

    if (error instanceof Error) {
      throw new TrafficConfigApiError(error.message, 400);
    }

    throw new TrafficConfigApiError('Unknown error', 400);
  }

  async getDatabaseIngressConfig(
    orchestra: string,
    tenantId: string,
    databaseId: string,
  ): Promise<IpfilterIngressFilter> {
    try {
      const api = await this.getIPFiltersApi();
      return await api.v1IpFiltersOrchestraTenantidDbidIngressConfigGet({
        orchestra,
        tenantid: tenantId,
        dbid: databaseId,
      });
    } catch (error) {
      return this.handleSdkError(error, {
        400: 'Unable to load IP filter for this orchestra',
        404: 'IP filter not found',
        500: 'Failed to load IP filter',
      });
    }
  }
}

export const trafficConfigApiRef: ApiRef<TrafficConfigRestApi> =
  createApiRef<TrafficConfigRestApi>({
    id: 'plugin.neo4j-networking.service',
  });
